import styled from "styled-components";
import ADD_RED_SVG from "assets/images/add_red.svg";
import IMG_SVG from "assets/images/img.svg";
import UPLOAD_SVG from "assets/images/upload.svg";
import PLAY_SVG from "assets/images/play.svg";
import STOP_SVG from "assets/images/stop.svg";
import DELETE_SVG from "assets/images/delete.svg";
import { word } from "shared/types/Report";
import { useState, useRef } from "react";
import {
    EditorTableWrap, EditorTable, EditorThead, EditorTbody, EditorTfoot, EditorTrHead, EditorTr, EditorTd, EditorTh,
    EditorScroll, EditorInput, EditorIconBtn, EditorCheck, EditorThumb, EditorAudioRow, TableEmpty
} from "../homeworkUi";

interface WordTableProps {
    list: word[],
    selectedList: number[],
    onAddClickHandler: () => void,
    onChangeHandler: (index: number, name: string, value: string) => void
    onSelectHandler: (id:number, value:boolean) => void,
    onImageChangeHandler: (index: number, event: React.ChangeEvent<HTMLInputElement>) => void;
    onSelectAllClickHandler: (value:boolean) => void,
    imageInputRefs: React.RefObject<(HTMLInputElement | null)[]>,
    wordAudioInputRefs: React.RefObject<(HTMLInputElement | null)[]>,
    exampleAudioInputRefs: React.RefObject<(HTMLInputElement | null)[]>,
    onImageInputClickHandler:(index: number) => void,
    onWordAudioInputClickHandler: (index: number) => void,
    onExampleAudioInputClickHandler: (index: number) => void,
    onWordAudioChangeHandler: (index: number, event: React.ChangeEvent<HTMLInputElement>) => void,
    onExampleAudioChangeHandler: (index: number, event: React.ChangeEvent<HTMLInputElement>) => void,
    onWordAudioDeleteHandler: (index: number) => void,
    onExampleAudioDeleteHandler: (index: number) => void,
    onFileClickHandler: (url:string, index:number, type:string) => void
}

const WordTable = ({ 
    list, 
    onAddClickHandler, 
    onChangeHandler, 
    onSelectHandler, 
    selectedList, 
    onSelectAllClickHandler, 
    imageInputRefs,
    wordAudioInputRefs,
    exampleAudioInputRefs,
    onImageInputClickHandler,
    onWordAudioInputClickHandler,
    onExampleAudioInputClickHandler,
    onWordAudioChangeHandler,
    onExampleAudioChangeHandler,
    onWordAudioDeleteHandler,
    onExampleAudioDeleteHandler,
    onImageChangeHandler, 
    onFileClickHandler
}: WordTableProps) => {
    const audioRefs = useRef<{[key: string]: HTMLAudioElement | null}>({});
    const [isPlayingList, setIsPlayingList] = useState<{[key: string]: boolean}>({});

    const handlePlayPause = (index: number, type: 'word' | 'example') => {
        const key = `${type}-${index}`;
        const audioRef = audioRefs.current[key];
        if (audioRef) {
            if (isPlayingList[key]) {
                audioRef.pause();
            } else {
                audioRef.play();
            }
            setIsPlayingList(prev => ({
                ...prev,
                [key]: !prev[key]
            }));
        }
    };

    const renderAudioPreview = (item: word, index: number, type: 'word' | 'example') => {
        const audio = type === 'word' ? item.word_audio : item.example_audio;
        if (audio) {
            const audioUrl = typeof audio === "string" ? audio : "";
            const key = `${type}-${index}`;
            return (
                <EditorAudioRow>
                    <audio ref={(el) => (audioRefs.current[key] = el)} src={audioUrl} />
                    <EditorIconBtn
                        src={isPlayingList[key] ? STOP_SVG : PLAY_SVG}
                        alt="play_img"
                        onClick={() => handlePlayPause(index, type)}
                    />
                    <EditorIconBtn
                        src={DELETE_SVG}
                        alt="delete_audio"
                        onClick={() => type === 'word' ? onWordAudioDeleteHandler(index) : onExampleAudioDeleteHandler(index)}
                    />
                </EditorAudioRow>
            );
        }
        return (
            <>
                <HiddenInput
                    type="file"
                    accept="audio/*"
                    ref={(el) => {
                        if (type === 'word' && wordAudioInputRefs.current) {
                            wordAudioInputRefs.current[index] = el;
                        } else if (type === 'example' && exampleAudioInputRefs.current) {
                            exampleAudioInputRefs.current[index] = el;
                        }
                    }}
                    onChange={(e) => type === 'word' ? onWordAudioChangeHandler(index, e) : onExampleAudioChangeHandler(index, e)}
                />
                <EditorIconBtn
                    src={UPLOAD_SVG}
                    alt="add_audio"
                    onClick={() => type === 'word' ? onWordAudioInputClickHandler(index) : onExampleAudioInputClickHandler(index)}
                />
            </>
        );
    };

    const renderImagePreview = (item:word, index:number) => {
        if(item.image_preview){
            return <EditorThumb src={item.image_preview} alt="preview" onClick={() => {
                if (item.image_preview) {
                    onFileClickHandler(item.image_preview, index, "image");
                }
            }}/>
        }else if(item.image){
            const imageUrl = typeof item.image === 'string' ? item.image : "";
            return <EditorThumb src={imageUrl} alt="preview" onClick={() => {
                if(imageUrl){
                    onFileClickHandler(imageUrl, index, "image");
                }
            }}/>
        }
        return (
            <>
                <HiddenInput type="file" accept="image/*" ref={(el) => { if(imageInputRefs.current) {imageInputRefs.current[index] = el;}}} onChange={(e) => onImageChangeHandler(index, e)}/>
                <EditorIconBtn src={IMG_SVG} alt="add_img" onClick={() => onImageInputClickHandler(index)} />
            </>
        );
    }

    const viewWordList = () => {
        if (!list.length) {
            return (
                <EditorTr>
                    <EditorTd colSpan={9}>
                        <TableEmpty>아직 단어가 없습니다. 아래 + 버튼으로 추가하거나 일괄등록을 사용하세요.</TableEmpty>
                    </EditorTd>
                </EditorTr>
            );
        }
        return list.map((item, index) => (
            <EditorTr key={index + 1}>
                <EditorTd style={{ width: "5%" }}>{index + 1}</EditorTd>
                <EditorTd style={{ width: "16%" }}>
                    <EditorInput name="word" value={item.word} onChange={(e) => onChangeHandler(index, e.target.name, e.target.value)} />
                </EditorTd>
                <EditorTd style={{ width: "8%" }}>{renderAudioPreview(item, index, 'word')}</EditorTd>
                <EditorTd style={{ width: "14%" }}>
                    <EditorInput name="kor" value={item.kor} onChange={(e) => onChangeHandler(index, e.target.name, e.target.value)} />
                </EditorTd>
                <EditorTd style={{ width: "22%" }}>
                    <EditorInput name="example" value={item.example} onChange={(e) => onChangeHandler(index, e.target.name, e.target.value)} placeholder="전체 문장을 입력해 주세요"/>
                </EditorTd>
                <EditorTd style={{ width: "8%" }}>{renderAudioPreview(item, index, 'example')}</EditorTd>
                <EditorTd style={{ width: "17%" }}>
                    <EditorInput name="example_kor" value={item.example_kor} onChange={(e) => onChangeHandler(index, e.target.name, e.target.value)}/>
                </EditorTd>
                <EditorTd style={{ width: "6%" }}>{renderImagePreview(item, index)}</EditorTd>
                <EditorTd style={{ width: "4%" }}>
                    <EditorCheck type="checkbox" checked={selectedList.includes(item.id)} onChange={(e) => onSelectHandler(item.id, e.target.checked)}/>
                </EditorTd>
            </EditorTr>
        ));
    }

    return (
        <EditorTableWrap>
            <EditorTable>
                <EditorThead>
                    <EditorTrHead>
                        <EditorTh style={{ width: "5%" }}>번호</EditorTh>
                        <EditorTh style={{ width: "16%" }}>단어</EditorTh>
                        <EditorTh style={{ width: "8%" }}>단어 음원</EditorTh>
                        <EditorTh style={{ width: "14%" }}>단어 뜻</EditorTh>
                        <EditorTh style={{ width: "22%" }}>예문</EditorTh>
                        <EditorTh style={{ width: "8%" }}>예문 음원</EditorTh>
                        <EditorTh style={{ width: "17%" }}>예문 해석</EditorTh>
                        <EditorTh style={{ width: "6%" }}>이미지</EditorTh>
                        <EditorTh style={{ width: "4%" }}>
                            <EditorCheck type="checkbox" onChange={(e) => onSelectAllClickHandler(e.target.checked)}/>
                        </EditorTh>
                    </EditorTrHead>
                </EditorThead>
            </EditorTable>
            <EditorScroll>
                <EditorTable>
                    <EditorTbody>
                        {viewWordList()}
                    </EditorTbody>
                </EditorTable>
            </EditorScroll>
            <EditorTable>
                <EditorTfoot>
                    <EditorTr>
                        <EditorTd colSpan={9}><EditorIconBtn src={ADD_RED_SVG} alt="add" onClick={onAddClickHandler} /></EditorTd>
                    </EditorTr>
                </EditorTfoot>
            </EditorTable>
        </EditorTableWrap>
    )
}

const HiddenInput = styled.input`
    display: none;
`;

export default WordTable;
